import React from "react";
import { Box, IconButton, Fade } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StepHeader from "./StepHeader";

/**
 * StepContainer - Wrapper for all step screens with header + close + back button
 *
 * @param {number} currentStep - Current step (1-based)
 * @param {number} totalSteps - Total number of steps
 * @param {Function} onClose - Close handler
 * @param {Function} onBack - Back handler (if null/undefined, back button is hidden)
 * @param {boolean} showHeader - Whether to show progress header
 * @param {React.ReactNode} children - Step content
 */
const StepContainer = ({
  currentStep = 1,
  totalSteps = 3,
  onClose,
  onBack,
  showHeader = true,
  children,
}) => {
  return (
    <Box
      sx={{
        maxWidth: { xs: "100vw", sm: "95vw", md: 700, lg: 800 },
        width: "100%",
        mx: "auto",
        bgcolor: "#fff",
        borderRadius: { xs: "24px", sm: 3, md: 4 },
        boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
        "@media (max-width: 600px)": {
          minWidth: "100vw",
          maxWidth: "100vw",
          borderRadius: "24px",
          boxShadow: "0 3px 12px rgba(0,0,0,0.09)",
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 10,
          color: "#6b7280",
          "&:hover": { color: "#374151", bgcolor: "#f3f4f6" },
        }}
        size="small"
      >
        <CloseIcon />
      </IconButton>

      {/* Content area */}
      <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: 3, pb: 3 }}>
        {/* Back button + Step header row */}
        {showHeader && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0 }}>
            {onBack && (
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  mt: -0.5,
                  color: "#6b7280",
                  "&:hover": { color: "#7C3AED", bgcolor: "#F3EEFF" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
            <Box sx={{ flex: 1 }}>
              <StepHeader currentStep={currentStep} totalSteps={totalSteps} />
            </Box>
          </Box>
        )}
        <Fade in timeout={400}>
          <Box>{children}</Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default StepContainer;
